import { ActivateAccountResult, LoginResult, ResendActivationResult, ResetPasswordRequestResult, ResetPasswordResult, SignUpResult, TwoFactorResult } from "./apiTypes";
import { Api } from "./api_to_state";
import { ActivateAccountData, BasicCredentials, ResendActivationData, ResetPasswordData, SignUpData, User } from "./authState";
import { Option, none, some } from "widgets-for-react"
import i18next from "i18next";
import { DataLayer } from "../../utils/datalayer";


const activateAccount = async (activationData: ActivateAccountData): Promise<ActivateAccountResult> => {
  let headers = new Headers()
  headers.append('content-type', 'application/json')

  let apiData = {
    ActivationToken: activationData.token,
    Email: activationData.email ? activationData.email.trim() : null
  }
  const response = await fetch(`/api/v1/authentication/activate`, {
    method: 'post', body: JSON.stringify(apiData), headers: headers
  });
  if (response.ok) {
    const json = await response.json()
    if (json.status === "success") DataLayer.confirmTrial() 
    return json;
  }
  const json = await Promise.reject(response);
  return json as ActivateAccountResult;}

const login = async (credentials_: BasicCredentials & { Role?: string }, useTwoFactor: string | false | undefined): Promise<LoginResult> => {
  let headers = new Headers()
  headers.append('content-type', 'application/json')

  const credentials: BasicCredentials & { Role?: string } = {
    ...credentials_,
    email: credentials_.email.trim()
  }

  const response = await fetch(`/api/v1/authentication/login`, {
    method: 'post',
    credentials: 'include',
    body: JSON.stringify(credentials),
    headers: headers
  })

  // HTTP Status Code "Too many requests" will handle the same as OK with status "error too many attempts"
  // We used to respond with OK code, but it is beneficial to report these as rate limit responses in Azure Monitor
  if(response.status === 429) {
    return { status: "error too many attempts" };
  }

  if (!response.ok) {
    return { status: "error generic not authorized" }
  }

  let json = await response.json()

  if (json.status === "error too many attempts") return json
  if (json.status === "error account not active") return { ...json, email: json.value }
  if (json.status === "error account blocked") return { status: json.status }

  if (json.status.includes("two factor")) {
    return { status: json.status }
  }

  if (json.status == "must reset password") {
    return { status: "must reset password" }
  }

  if (json.role == "choose role") {
    return { status: "choose role", roles: json.value }
  }

  if (json.status != "success") {
    return { status: "error generic not authorized" }
  }

  const user = json.value
  DataLayer.successfulLogin(user.id.toString())
  return {
    status: "success",
    role: json.role,
    session: json.session,
    user: {
      email: user.email,
      id: user.id,
      username: user.username,
    }
  }

}

const isSessionValid = async (): Promise<Option<LoginResult>> => {
  // let role = localStorage.getItem("role")
  // let session = localStorage.getItem("session")
  // let localStorageUser = localStorage.getItem("user")
  //let session = getSession()
  let headers = new Headers()
  headers.append('content-type', 'application/json')

  const response = await fetch(`/api/v1/authentication/IsSessionValid`, {
    method: 'post',
    body: null,
    headers: headers
  }
  )

  if (response.ok) {
    const sessionValidation = await response.json()

    if (sessionValidation.status == "invalid") {
      return none()
    }

    return some<LoginResult>({
      user: sessionValidation.value,
      role: sessionValidation.role,
      session: sessionValidation.session,
      status: "success"
    })
  }
  return none()
}

const registerAccount = async (signupData: SignUpData, originPath: string): Promise<SignUpResult> => {
  let headers = new Headers()
  headers.append('content-type', 'application/json')

  let registerData = {
    Name: signupData.name,
    Surname: signupData.surname,
    Email: signupData.email?.trim(),
    Password: signupData.password,
    MarketingEmails: signupData.marketingEmails,
    Job: signupData.selectedJob.input.Title,
    PromoCode: signupData.promoCode
  }
  const response = await fetch(`/api/v1/authentication/register?originPath=${originPath}&originLanguage=${i18next.language}&isStudent=${signupData.isStudent}`, {
    method: 'post', body: JSON.stringify(registerData), headers: headers
  });
  if (response.ok) {
    const json = await response.json()
    if (json.status === "success") DataLayer.successOnTrialForm()
    if (json.status === "account already exists") DataLayer.errorOnTrialForm("account already exists")
    return json;
  }
  const json = await Promise.reject(response);
  return json as SignUpResult;
}

export type PasswordScoreTypes = "strong" | "almost good" | "weak"

export const scorePassword = (password: string): { score: number, res: PasswordScoreTypes } => {
  if (password == undefined)
    return { score: 0, res: "weak" }
  if (password.length >= 8)
    return { score: 100, res: "strong" }
  return { score: 0, res: "weak" }
}

const logout = async (u: User, s: string): Promise<{}> => {
  let headers = new Headers()
  headers.append('content-type', 'application/json')

  await fetch("/api/v1/authentication/logout",
    {
      method: 'post', credentials: 'include',
      body: JSON.stringify({
        SessionId: s
      }),
      headers: headers
    })

  return {}
}

const requestResetPassword = async (email: string): Promise<ResetPasswordRequestResult> => {
  let headers = new Headers()
  headers.append('content-type', 'application/json')

  const response = await fetch(`/api/v1/authentication/ForgotPassword?originLanguage=${i18next.language}`,
    {
      method: 'post', credentials: 'include',
      body: JSON.stringify({
        Email: email.trim()
      }),
      headers: headers
    }
  )

  if (response.ok) {
    return response.json();
  }
  const json = await Promise.reject(response);
  return json as ResetPasswordRequestResult;
}

const resetPassword = async (data: ResetPasswordData): Promise<ResetPasswordResult> => {
  let headers = new Headers()
  headers.append('content-type', 'application/json')

  let apiData = {
    Token: data.reset_password_token,
    Email: data.email.trim(),
    Password: data.password
  }

  const response = await fetch("/api/v1/authentication/ResetPassword",
    {
      method: 'post', credentials: 'include',
      body: JSON.stringify(apiData),
      headers: headers
    }
  )

  if (response.ok) {
    return response.json();
  }
  const json = await Promise.reject(response);
  return json as ResetPasswordResult;
}

const resendAccountActivation = async (data_ : ResendActivationData, originPath: string): Promise<ResendActivationResult> => {
  let headers = new Headers()
  headers.append('content-type', 'application/json')

  const data: ResendActivationData = {
    ...data_,
    email: data_.email ? data_.email.trim() : null
  }
  const response = await fetch(`/api/v1/authentication/resend-activation-email?originPath=${originPath}&originLanguage=${i18next.language}`,
    {
      method: 'post', credentials: 'include',
      body: JSON.stringify(data),
      headers: headers
    }
  )

  if (response.ok) {
    return response.json()
  }
  const json = await Promise.reject(response);
  return json as ResendActivationResult;
}

export const AuthApi: Api = {
  activate_account: activateAccount,
  sign_up: registerAccount,
  scorePassword: scorePassword,
  activate_account_with_reset: activateAccount,
  login: login,
  logout: logout,
  request_new_pin:() => new Promise<TwoFactorResult>((resolve) => 
    setTimeout(() => resolve({ status:"success" }), 200)
  ),
  request_reset_password: requestResetPassword,
  resend_account_activation: resendAccountActivation,
  reset_password: resetPassword,
  isSessionValid: isSessionValid
}